/**
 * Collapsible reasoning block — the transcript's rendering of a model's
 * thinking, modelled on Claude Code's.
 *
 * Lifecycle, driven entirely by the message's `isThinking` flag (see
 * `handleFrame`'s `stream: 'thinking'` branch):
 *
 *   thinking   → header shimmers "Thinking", body auto-expanded so the trace
 *                is readable as it streams in
 *   answer starts / run ends → header settles to "Thought for 4s", body
 *                collapses
 *
 * A run usually gets ONE block — a snapshot emitter whose payload is the
 * current block rather than the whole turn gets one per phase, since nothing
 * ties those together. Interleaved thinking — reasoning that resumes after
 * the answer has begun — grows this block in place (see
 * `applyThinkingUpdate`) rather than adding a second one, and it grows
 * COLLAPSED: a block never returns to the live state once it has left it, so
 * a trace the reader had finished with is never yanked back open, and a frame
 * arriving after the run's terminator can't strand a block nothing will
 * close. The duration therefore measures the one live phase.
 *
 * A click at any point pins the disclosure state, and the auto-collapse stops
 * fighting the user from then on. Reasoning is rendered as plain pre-wrapped
 * text rather than markdown: traces are full of half-finished lists and stray
 * `#`/`*` that a markdown pass turns into headings and bullets mid-stream,
 * which reads as corruption rather than thought.
 */
import { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHAT_PROSE } from '../../../styles/chat-typography';

import {
  DisclosureCaret,
  DisclosureHeader,
  DisclosureLabel,
  DisclosureWrap,
} from './disclosure-chrome';

/** Round a thinking duration the way a reader would say it out loud. */
function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 1) return 'Thought for less than a second';
  if (seconds < 60) return `Thought for ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0
    ? `Thought for ${minutes}m`
    : `Thought for ${minutes}m ${rest}s`;
}

export function ThinkingBlock({
  thinking,
  isThinking,
}: {
  readonly thinking: string;
  readonly isThinking: boolean;
}) {
  // `null` = follow the lifecycle (expanded while thinking, collapsed after);
  // a boolean = the user has taken over.
  const [pinned, setPinned] = useState<boolean | null>(null);
  const expanded = pinned ?? isThinking;
  const bodyId = useId();

  // How long the model spent thinking, measured by this component's own
  // clock: from the first render that sees the block live, to the render that
  // sees it end. NOT from the message timestamp — that is deliberately
  // anchored to the message the block follows (so a sync merge can't sort the
  // reasoning below the answer it explains), which makes it the user's send
  // time, not the model's first reasoning token. Frozen once measured:
  // reading `Date.now()` at render instead would keep the number growing for
  // the rest of the session, since a collapsed block re-renders whenever the
  // transcript does.
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const phaseStartedAt = useRef<number | null>(isThinking ? Date.now() : null);
  const wasThinking = useRef(isThinking);
  useEffect(() => {
    if (wasThinking.current && !isThinking) {
      const start = phaseStartedAt.current;
      if (start != null) setElapsedMs(Date.now() - start);
    } else if (!wasThinking.current && isThinking) {
      phaseStartedAt.current = Date.now();
    }
    wasThinking.current = isThinking;
  }, [isThinking]);

  // Follow the tail while the trace streams into an expanded block, so the
  // newest reasoning is the part on screen. Skipped once the user pins the
  // block open — they're reading, not watching.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isThinking || pinned != null) return;
    const body = bodyRef.current;
    if (body == null) return;
    body.scrollTop = body.scrollHeight;
  }, [thinking, isThinking, pinned]);

  const label = isThinking
    ? 'Thinking'
    : elapsedMs != null
      ? formatDuration(elapsedMs)
      : 'Thought';

  // The live region exists only for a block that has been live in THIS
  // instance — a reasoning phase this reader is present for. Rendering it
  // unconditionally made every restored block announce "Thought" on
  // transcript load, and left one competing `aria-live` region per historical
  // block for the rest of the session.
  const wasEverLive = useRef(isThinking);
  if (isThinking) wasEverLive.current = true;

  // Filled one effect AFTER mount, deliberately: a region inserted into the
  // DOM with its text already in place is inconsistently announced —
  // assistive tech watches an EXISTING region for changes — so mounting it
  // empty and then writing the label makes the first announcement
  // ("Thinking") a change like every later one.
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    if (!wasEverLive.current) return;
    setAnnounced(label);
  }, [label]);

  return (
    <DisclosureWrap>
      {/* Suppressing the transcript's ProcessingRow while a block is live
          takes away the only `role="status"` region on screen, and the
          shimmer that replaces it is purely visual. Without this line the
          reasoning phase — the default now, and often the longest part of a
          turn — is entirely silent to a screen reader. */}
      {wasEverLive.current && (
        <LiveStatus role="status" aria-live="polite">
          {announced}
        </LiveStatus>
      )}
      <DisclosureHeader
        type="button"
        aria-expanded={expanded}
        // Only while the body exists: the IDREF would otherwise dangle in the
        // state most blocks spend their life in.
        aria-controls={expanded ? bodyId : undefined}
        onClick={() => {
          setPinned(!expanded);
        }}
      >
        <DisclosureCaret $open={expanded} aria-hidden="true">
          ▸
        </DisclosureCaret>
        <DisclosureLabel $live={isThinking}>{label}</DisclosureLabel>
      </DisclosureHeader>
      {expanded && (
        // The trace scrolls inside its own box, so it needs to be focusable to
        // be scrollable from the keyboard at all — the same treatment
        // `MessageBubble` gives an overflowing markdown table.
        <Body
          id={bodyId}
          ref={bodyRef}
          $tall={pinned != null || !isThinking}
          role="region"
          aria-label="Reasoning"
          tabIndex={0}
        >
          {thinking}
        </Body>
      )}
    </DisclosureWrap>
  );
}

/**
 * Announces the block's state without drawing it twice: the visible label
 * carries the same words, so this is clipped rather than hidden — `display:
 * none` and `visibility: hidden` both remove it from the accessibility tree.
 */
const LiveStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const Body = styled.div<{ $tall: boolean }>`
  ${CHAT_PROSE}
  margin-top: 6px;
  padding-left: 10px;
  border-left: 2px solid var(--border-color);
  color: var(--text-secondary);
  font-style: italic;
  /* Traces arrive as hard-wrapped plain text — preserve the model's own line
     breaks instead of reflowing them into one paragraph. */
  white-space: pre-wrap;
  word-break: break-word;
  /* A long trace must not push the answer off-screen. The short box is for
     the streaming case the reader is watching go by; the moment they take
     control — by pinning it open, or by re-opening it after it collapsed —
     they are READING, and the tail-follow stops too, so give them the tall
     one. Keying the height off liveness alone gave a pinned-open live trace
     the small viewport AND no auto-scroll: the worst of both. */
  max-height: ${({ $tall }) => ($tall ? '24em' : '11em')};
  overflow-y: auto;
`;
